import { useEffect, useState, type FC } from "react";

import { AgentPeek } from "@/components/brand";
import { Kbd } from "@/components/ui/kbd";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useRunningTurnsStore } from "@/features/agents/runningTurnsStore";

import { caseyShortcutKeys, caseyShortcutLabel } from "./caseyShortcut";
import { ideLayoutStore, useIdeLayout } from "./ideLayoutStore";

/** His duck under the edge when the panel opens (`agent-peek-duck`). */
export const DOCK_DUCK_MS = 160;

type Props = {
  individualId: string;
};

/**
 * Casey, waving from the IDE's bottom-right edge while his panel is hidden.
 * He pops up out of the edge when the panel collapses, waves as he does on
 * the tab bar, and ducks back under it when the panel opens. A dot shows
 * while one of my turns on this case is running.
 */
export const CaseyDock: FC<Props> = ({ individualId }) => {
  const collapsed = useIdeLayout().collapsed.right;
  const working = useRunningTurnsStore((s) =>
    s.turns.some((t) => t.scope.kind === "case" && t.scope.id === individualId)
  );
  const [present, setPresent] = useState(collapsed);
  useEffect(() => {
    if (collapsed) {
      setPresent(true);
      return undefined;
    }
    const t = window.setTimeout(() => setPresent(false), DOCK_DUCK_MS);
    return () => window.clearTimeout(t);
  }, [collapsed]);
  if (!present) return null;
  const leaving = !collapsed;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid="casey-dock"
          aria-label="Open Casey"
          aria-keyshortcuts={caseyShortcutKeys()}
          disabled={leaving}
          onClick={() => ideLayoutStore.show("right")}
          className="absolute right-4 bottom-0 z-20 flex rounded-t-md px-1 pt-1 outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <span className="relative">
            <span
              className="agent-peek-stage block [--peek-pop-delay:140ms]"
              data-leaving={leaving || undefined}
            >
              <AgentPeek className="h-8" />
            </span>
            {working ? (
              <span
                aria-hidden
                data-testid="casey-dock-working"
                className="absolute -top-0.5 right-0 size-2 animate-pulse rounded-full bg-primary ring-cutout ring-surface motion-reduce:animate-none"
              />
            ) : null}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="left" className="flex items-center gap-2">
        Open Casey <Kbd>{caseyShortcutLabel()}</Kbd>
      </TooltipContent>
    </Tooltip>
  );
};
